/**
 * P2.1: Storage Service
 * Centralized localStorage/sessionStorage abstraction for easy backend swap
 * 
 * All data operations go through this service.
 * When backend is ready, replace storage calls with API calls.
 * 
 * P2.2 UPDATE: Added session storage support for transient data
 */

const STORAGE_PREFIX = 'treatx_';

/**
 * P0-AUTH-IDENTITY-SWEEP-01 — identity-bearing keys that do NOT use
 * `STORAGE_PREFIX` and therefore escaped the O84-C sweep. Each one was
 * verified to survive a real logout in Preview.
 *
 *   treatx.mfa.*            previous user's e-mail AND a privileged MFA
 *                           challenge token (MfaChallengePage/MfaEnrollPage
 *                           read these back to render the identity)
 *   treatx_agent_token/-    a SEPARATE bearer identity which
 *     treatx_agent           `AgentAuthContext` installs onto the SHARED
 *                           axios client (`api.defaults.headers.common`) on
 *                           every app boot — app-wide provider (App.js)
 *   treatx_patient_flow     patient journey state (localStorage)
 *   patientFlowState        patient journey state (sessionStorage)
 *   patientTimelines        patient treatment timelines
 *   repPatientConversations sales-rep ↔ patient conversation cache
 *   intakePreferences       patient intake answers
 *   redirectAfterLogin      previous identity's intended destination
 *   trialWarningShown       previous clinic's suppressed trial warning
 *   treatx_treatment_intent / treatx_pending_quote / preselectedTreatment*
 *                           pre-login intent tied to the person who left
 *
 * Deliberately NOT swept: cookie-consent records (a compliance artefact),
 * `i18nextLng`, UI collapse/layout preferences, analytics device ids, and
 * the Google `g_state` account-chooser cookie — that one is browser/Google
 * owned and must never be treated as TreatX state.
 */
const IDENTITY_SCOPED_UNPREFIXED_KEYS = [
  'treatx.mfa.token', 'treatx.mfa.email', 'treatx.mfa.expiresInMinutes',
  'treatx_agent_token', 'treatx_agent',
  'treatx_patient_flow', 'patientFlowState', 'patientTimelines',
  'repPatientConversations', 'intakePreferences',
  'redirectAfterLogin', 'trialWarningShown',
  'treatx_treatment_intent', 'treatx_pending_quote',
  'preselectedTreatment', 'preselectedTreatmentId',
];


/**
 * Storage Service - localStorage abstraction
 */
const StorageService = {
  /**
   * Get data from storage
   * @param {string} key - Storage key (without prefix)
   * @returns {*} Parsed data or null
   */
  get(key) {
    try {
      const data = localStorage.getItem(STORAGE_PREFIX + key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`[StorageService] Error reading ${key}:`, error);
      return null;
    }
  },

  /**
   * Set data in storage
   * @param {string} key - Storage key (without prefix)
   * @param {*} data - Data to store (will be JSON stringified)
   */
  set(key, data) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(data));
    } catch (error) {
      console.error(`[StorageService] Error writing ${key}:`, error);
    }
  },

  /**
   * Remove data from storage
   * @param {string} key - Storage key (without prefix)
   */
  remove(key) {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch (error) {
      console.error(`[StorageService] Error removing ${key}:`, error);
    }
  },

  /**
   * Clear all TreatX data from storage
   */
  clear() {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(STORAGE_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('[StorageService] Error clearing storage:', error);
    }
  },

  /**
   * Get all keys with prefix
   * @returns {string[]} Array of keys
   */
  keys() {
    return Object.keys(localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .map(key => key.replace(STORAGE_PREFIX, ''));
  },
  
  // =============================================================================
  // P2.2: Session Storage Methods (for transient data like demo CRM data)
  // =============================================================================
  
  /**
   * Get data from session storage
   * @param {string} key - Storage key (without prefix)
   * @returns {*} Parsed data or null
   */
  getSession(key) {
    try {
      const data = sessionStorage.getItem(STORAGE_PREFIX + key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`[StorageService] Error reading session ${key}:`, error);
      return null;
    }
  },

  /**
   * Set data in session storage
   * @param {string} key - Storage key (without prefix)
   * @param {*} data - Data to store (will be JSON stringified)
   */
  setSession(key, data) {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(data));
    } catch (error) {
      console.error(`[StorageService] Error writing session ${key}:`, error);
    }
  },

  /**
   * Remove data from session storage
   * @param {string} key - Storage key (without prefix)
   */
  removeSession(key) {
    try {
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch (error) {
      console.error(`[StorageService] Error removing session ${key}:`, error);
    }
  },

  /**
   * Clear all TreatX data from session storage
   */
  clearSession() {
    try {
      Object.keys(sessionStorage)
        .filter(key => key.startsWith(STORAGE_PREFIX))
        .forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
      console.error('[StorageService] Error clearing session storage:', error);
    }
  },

  /**
   * Get all session storage keys with prefix
   * @returns {string[]} Array of keys
   */
  sessionKeys() {
    return Object.keys(sessionStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .map(key => key.replace(STORAGE_PREFIX, ''));
  },

  /**
   * O84-C — drop every cache that belongs to the SIGNED-IN IDENTITY.
   *
   * These session caches hold one clinic's patients, leads, offers and
   * profile. They used to survive logout, so the next person signing in
   * in the same tab could read the previous clinic's cached CRM data
   * before any request was made. Called from the canonical logout paths.
   * Consent records and UI preferences are deliberately NOT touched.
   *
   * P0-AUTH-IDENTITY-SWEEP-01 extends the sweep to the identity-bearing
   * keys that do NOT carry `STORAGE_PREFIX` and were therefore missed —
   * verified surviving a real Preview logout. See the key list above for
   * why each one is identity-scoped.
   */
  clearIdentityScopedCaches() {
    [
      'patients', 'leads', 'offers', 'clinic_profiles', 'clinic_user',
      'clinic_settings', 'clinic_accounts_state', 'inactive_sales_reps',
      'inactive_doctors', 'data_version',
    ].forEach((key) => {
      try {
        sessionStorage.removeItem(STORAGE_PREFIX + key);
        localStorage.removeItem(STORAGE_PREFIX + key);
      } catch { /* storage disabled — non-fatal */ }
    });
    IDENTITY_SCOPED_UNPREFIXED_KEYS.forEach((key) => {
      try {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      } catch { /* storage disabled — non-fatal */ }
    });
    // Removing `treatx_agent_token` from storage is not enough on its own:
    // `AgentAuthContext` has already installed it as a DEFAULT header on the
    // shared axios client for the lifetime of this page. That header is owned
    // by that context, which drops it (and its React state) on the
    // `treatx:identity-changed` event both logout paths dispatch — storage
    // layer stays free of any HTTP-client dependency.
  },
};

// Storage keys constants
export const STORAGE_KEYS = {
  // Auth
  USER: 'user',
  SESSION: 'session',
  TOKEN: 'token',
  
  // Clinic
  CLINIC_ACCOUNTS: 'clinic_accounts',
  CURRENT_CLINIC: 'current_clinic',
  
  // Applications
  APPLICATIONS: 'applications',
  DRAFT_APPLICATION: 'draft_application',
  
  // Subscriptions
  SUBSCRIPTIONS: 'subscriptions',
  CURRENT_SUBSCRIPTION: 'current_subscription',
  
  // Email Templates
  EMAIL_TEMPLATES: 'email_templates_v2',
  
  // Admin
  ADMIN_USER: 'admin_user',
  ADMIN_SESSION: 'admin_session',
  
  // Packages
  PACKAGE_CATALOG: 'package_catalog',
  
  // P2.2: Session Storage Keys (for transient CRM data)
  // These use session storage methods (getSession, setSession, etc.)
  DATA_VERSION: 'data_version',
  PATIENTS: 'patients',
  LEADS: 'leads',
  OFFERS: 'offers',
  CLINIC_PROFILES: 'clinic_profiles',
  CLINIC_USER: 'clinic_user',
  CLINIC_ACCOUNTS_STATE: 'clinic_accounts_state',
  CLINIC_SETTINGS: 'clinic_settings',
  INACTIVE_SALES_REPS: 'inactive_sales_reps',
  INACTIVE_DOCTORS: 'inactive_doctors',
};

export default StorageService;
